import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Tabs, 
  TabsContent
} from "@/components/ui/tabs";
import { useSetupGuide } from "@/hooks/useSetupGuide";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WelcomeStep } from "./steps/WelcomeStep";
import { CategoriesStep } from "./steps/CategoriesStep";
import { ContractTypesStep } from "./steps/ContractTypesStep";
import { CurrenciesStep } from "./steps/CurrenciesStep";
import { ExchangeRateStep } from "./steps/ExchangeRateStep";
import { CompanyRegisterStep } from "./steps/CompanyRegisterStep";
import { ImportanceColorsStep } from "./steps/ImportanceColorsStep";
import { CompletionStep } from "./steps/CompletionStep";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

const STEPS = [
  { id: "welcome", label: "Welcome" },
  { id: "categories", label: "Categories" },
  { id: "contract-types", label: "Contract Types" },
  { id: "currencies", label: "Currencies" },
  { id: "exchange-rates", label: "Exchange Rates" },
  { id: "company-register", label: "Company Register" },
  { id: "importance-colors", label: "Importance Colors" },
  { id: "completed", label: "Completed" },
];

interface SetupStepsProps {
  onComplete: () => void;
  initialStep?: "categories" | "contract-types" | "currencies";
}

export function SetupSteps({ onComplete, initialStep }: SetupStepsProps) {
  const [currentStep, setCurrentStep] = useState("welcome");
  const [isCompleting, setIsCompleting] = useState(false);
  const { markSetupComplete } = useSetupGuide();
  const stepsContainerRef = useRef<HTMLDivElement>(null);
  
  const currentIndex = STEPS.findIndex(step => step.id === currentStep);
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === STEPS.length - 1;
  
  const scrollToActiveStep = () => {
    if (!stepsContainerRef.current) return;
    
    const container = stepsContainerRef.current;
    const activeStep = container.querySelector('[data-state="on"]') as HTMLElement;
    
    if (activeStep) {
      const containerRect = container.getBoundingClientRect();
      const activeStepRect = activeStep.getBoundingClientRect();
      
      const isStepInView = (
        activeStepRect.left >= containerRect.left &&
        activeStepRect.right <= containerRect.right
      );
      
      if (!isStepInView) {
        const scrollPosition = activeStepRect.left + 
                              (activeStepRect.width / 2) - 
                              (containerRect.width / 2) -
                              containerRect.left +
                              container.scrollLeft;
        
        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };
  
  useEffect(() => {
    scrollToActiveStep();
    
    const timeoutId = setTimeout(scrollToActiveStep, 100);
    return () => clearTimeout(timeoutId);
  }, [currentStep]);
  
  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
      return;
    }
    
    const nextStep = STEPS[currentIndex + 1];
    setCurrentStep(nextStep.id);
  };
  
  const handleBack = () => {
    if (isFirstStep) return;
    
    const prevStep = STEPS[currentIndex - 1];
    setCurrentStep(prevStep.id);
  };
  
  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      const success = await markSetupComplete();
      if (success) {
        toast.success("Setup completed successfully!");
        onComplete();
      } else {
        toast.error("Error marking setup as complete");
      }
    } catch (error) {
      console.error("Error completing setup:", error);
      toast.error("An error occurred while completing setup");
    } finally {
      setIsCompleting(false);
    }
  };
  
  const handleSkip = async () => {
    setIsCompleting(true);
    try {
      await markSetupComplete();
      toast.success("Setup skipped. You can access it anytime from Settings.");
      onComplete();
    } catch (error) {
      console.error("Error skipping setup:", error);
    } finally {
      setIsCompleting(false);
    }
  };
  
  const handleStepClick = (stepId: string) => {
    setCurrentStep(stepId);
  };
  
  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-4 pb-4 bg-background">
        <div className="w-full bg-muted h-2 rounded-full mb-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all" 
            style={{ width: `${(currentIndex / (STEPS.length - 1)) * 100}%` }}
          ></div>
        </div>
        
        <div className="w-full overflow-x-auto pb-2 scrollbar-none" ref={stepsContainerRef}>
          <ToggleGroup 
            type="single" 
            value={currentStep}
            onValueChange={(value) => value && setCurrentStep(value)} 
            className="flex space-x-1 min-w-max"
          >
            {STEPS.map((step, index) => (
              <ToggleGroupItem 
                key={step.id} 
                value={step.id}
                variant="outline"
                size="sm"
                disabled={isCompleting}
                className="whitespace-nowrap flex items-center gap-1"
                onClick={() => handleStepClick(step.id)}
              >
                {index < currentIndex ? (
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                ) : (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-muted text-xs">
                    {index + 1}
                  </span>
                )}
                <span>{step.label}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>
      
      <div className="flex-1 overflow-hidden">
        <Tabs value={currentStep} className="h-full">
          <ScrollArea className="h-full">
            <div className="px-6 py-4">
              <TabsContent value="welcome" className="mt-0 data-[state=active]:block">
                <WelcomeStep />
              </TabsContent>
              
              <TabsContent value="categories" className="mt-0 data-[state=active]:block">
                <CategoriesStep />
              </TabsContent>
              
              <TabsContent value="contract-types" className="mt-0 data-[state=active]:block">
                <ContractTypesStep />
              </TabsContent>
              
              <TabsContent value="currencies" className="mt-0 data-[state=active]:block">
                <CurrenciesStep />
              </TabsContent>
              
              <TabsContent value="exchange-rates" className="mt-0 data-[state=active]:block">
                <ExchangeRateStep />
              </TabsContent>
              
              <TabsContent value="company-register" className="mt-0 data-[state=active]:block">
                <CompanyRegisterStep />
              </TabsContent>
              
              <TabsContent value="importance-colors" className="mt-0 data-[state=active]:block">
                <ImportanceColorsStep />
              </TabsContent>
              
              <TabsContent value="completed" className="mt-0 data-[state=active]:block">
                <CompletionStep />
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>
      </div>
      
      <div className="px-6 py-4 border-t bg-background">
        <div className="flex justify-between">
          <div>
            {!isFirstStep && (
              <Button 
                variant="outline" 
                onClick={handleBack}
                disabled={isCompleting}
                className="flex items-center"
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex space-x-2">
            {!isLastStep && (
              <Button 
                variant="outline" 
                onClick={handleSkip}
                disabled={isCompleting}
              >
                Skip
              </Button>
            )}
            
            <Button 
              onClick={handleNext}
              disabled={isCompleting}
              className="flex items-center"
            >
              {isCompleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isLastStep ? "Completing..." : "Processing..."}
                </>
              ) : (
                <>
                  {isLastStep ? "Complete Setup" : "Next Step"}
                  {!isLastStep && <ChevronRight className="ml-1 h-4 w-4" />}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
